/**
 * Full Quiz Audit Runner - Phase 2
 * 
 * Comprehensive audit system that generates detailed reports on quiz coverage
 * across all 60+ course types in the platform.
 * 
 * Usage:
 * - Import and run in console: import { runFullQuizAudit } from '@/scripts/runFullQuizAudit'
 * - Or call from admin dashboard
 */

import { comprehensiveQuizAuditor } from '@/utils/comprehensiveQuizAudit';
import { validateAllQuizzes, generateQualityReport } from '@/utils/quizQualityAssurance';
import { QuizSystemFixer } from '@/utils/quizSystemFixer';

export interface FullAuditReport {
  timestamp: string;
  executionTime: number;
  summary: {
    totalCourses: number;
    totalModules: number;
    modulesWithQuizzes: number;
    modulesWithoutQuizzes: number;
    coveragePercentage: number;
    qualityGrade: 'A' | 'B' | 'C' | 'D' | 'F';
    overallQualityScore: number;
  };
  comprehensiveAudit: any;
  qualityAssurance: any;
  fixPlan: string;
  priorityActions: Array<{
    priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
    action: string;
    impact: string;
    estimatedTime: string;
  }>;
  courseTypeBreakdown: Record<string, {
    coursesCount: number;
    totalModules: number;
    quizCoverage: number;
    averageQuality: number;
    status: 'excellent' | 'good' | 'needs-improvement' | 'critical';
  }>;
  missingQuizzesByPriority: {
    critical: Array<{ courseId: string; moduleId: string; courseType: string; reason: string }>;
    high: Array<{ courseId: string; moduleId: string; courseType: string; reason: string }>;
    medium: Array<{ courseId: string; moduleId: string; courseType: string; reason: string }>;
    low: Array<{ courseId: string; moduleId: string; courseType: string; reason: string }>;
  };
}

/**
 * Run comprehensive quiz audit across all courses
 */
export const runFullQuizAudit = (): FullAuditReport => {
  const startTime = performance.now();
  console.log('🚀 Starting Full Quiz Audit System...\n');

  // Phase 1: Run comprehensive audit
  console.log('📊 Phase 1: Running comprehensive audit across all course types...');
  const comprehensiveAudit = comprehensiveQuizAuditor.auditAllQuizzes();
  console.log(`✅ Audited ${comprehensiveAudit.totalCourses} courses with ${comprehensiveAudit.totalModules} modules\n`);

  // Phase 2: Run quality assurance checks
  console.log('🔍 Phase 2: Running quality assurance validation...');
  const qualityAssurance = validateAllQuizzes();
  console.log(`✅ Quality check complete\n`);

  // Phase 3: Generate fix plan
  console.log('🛠️  Phase 3: Generating fix plan...');
  const quizFixer = new QuizSystemFixer();
  const fixPlan = quizFixer.generateFixReport();
  console.log(`✅ Fix plan generated\n`);

  // Calculate summary statistics
  const coveragePercentage = (comprehensiveAudit.modulesWithQuizzes / comprehensiveAudit.totalModules) * 100;
  const qualityScore = comprehensiveAudit.overallQualityScore;
  
  const qualityGrade = 
    qualityScore >= 90 ? 'A' :
    qualityScore >= 80 ? 'B' :
    qualityScore >= 70 ? 'C' :
    qualityScore >= 60 ? 'D' : 'F';

  // Categorize missing quizzes by priority
  const missingQuizzesByPriority = categorizeMissingQuizzes(comprehensiveAudit);

  // Generate priority actions
  const priorityActions = generatePriorityActions(comprehensiveAudit, coveragePercentage, qualityScore);

  // Enhanced course type breakdown
  const courseTypeBreakdown = enhanceCourseTypeBreakdown(comprehensiveAudit.courseTypeBreakdown);

  const endTime = performance.now();
  const executionTime = Math.round(endTime - startTime);

  const report: FullAuditReport = {
    timestamp: new Date().toISOString(),
    executionTime,
    summary: {
      totalCourses: comprehensiveAudit.totalCourses,
      totalModules: comprehensiveAudit.totalModules,
      modulesWithQuizzes: comprehensiveAudit.modulesWithQuizzes,
      modulesWithoutQuizzes: comprehensiveAudit.modulesWithoutQuizzes,
      coveragePercentage: parseFloat(coveragePercentage.toFixed(2)),
      qualityGrade,
      overallQualityScore: parseFloat(qualityScore.toFixed(2))
    },
    comprehensiveAudit,
    qualityAssurance,
    fixPlan,
    priorityActions,
    courseTypeBreakdown,
    missingQuizzesByPriority
  };

  // Print summary to console
  printAuditSummary(report);

  return report;
};

/**
 * Categorize missing quizzes by priority
 */
const categorizeMissingQuizzes = (audit: any) => {
  const missing = {
    critical: [] as any[],
    high: [] as any[],
    medium: [] as any[],
    low: [] as any[]
  };

  audit.courseResults.forEach((course: any) => {
    course.modulesWithoutQuizzes.forEach((moduleId: string) => {
      const item = {
        courseId: course.courseId,
        moduleId,
        courseType: course.courseType,
        reason: ''
      };

      // Categorize by course level and importance
      if (course.courseType.includes('fundamental') || course.courseType.includes('basic')) {
        item.reason = 'Beginner course - high user impact';
        missing.critical.push(item);
      } else if (course.courseType.includes('advanced') || course.courseType.includes('expert')) {
        item.reason = 'Advanced course - affects completion rate';
        missing.medium.push(item);
      } else if (course.quizQualityScore < 50) {
        item.reason = 'Low overall course quality';
        missing.high.push(item);
      } else {
        item.reason = 'Standard priority';
        missing.low.push(item);
      }
    });
  });

  return missing;
};

/**
 * Generate priority actions based on audit results
 */
const generatePriorityActions = (audit: any, coverage: number, quality: number) => {
  const actions: any[] = [];

  // Critical: Low coverage
  if (coverage < 70) {
    actions.push({
      priority: 'CRITICAL' as const,
      action: `Immediately address ${audit.modulesWithoutQuizzes} missing quizzes (${(100 - coverage).toFixed(1)}% gap)`,
      impact: 'Blocks user progression and certification eligibility',
      estimatedTime: `${Math.ceil(audit.modulesWithoutQuizzes * 0.5)} hours`
    });
  }

  // High: Low quality
  if (quality < 70) {
    actions.push({
      priority: 'HIGH' as const,
      action: 'Improve quiz quality - add explanations and difficulty levels',
      impact: 'Enhances learning outcomes and user satisfaction',
      estimatedTime: '8-12 hours'
    });
  }

  // High: Course types with <50% coverage
  const lowCoverageCourseTypes = Object.entries(audit.courseTypeBreakdown)
    .filter(([_, breakdown]: any) => breakdown.quizCoverage < 50)
    .map(([type]) => type);

  if (lowCoverageCourseTypes.length > 0) {
    actions.push({
      priority: 'HIGH' as const,
      action: `Focus on ${lowCoverageCourseTypes.length} course types with <50% coverage: ${lowCoverageCourseTypes.join(', ')}`,
      impact: 'Ensures baseline quality across all offerings',
      estimatedTime: '6-10 hours'
    });
  }

  // Medium: Standardization
  if (coverage >= 70 && coverage < 90) {
    actions.push({
      priority: 'MEDIUM' as const,
      action: 'Standardize quiz format across all courses',
      impact: 'Improves consistency and user experience',
      estimatedTime: '4-6 hours'
    });
  }

  // Low: Optimization
  if (coverage >= 90) {
    actions.push({
      priority: 'LOW' as const,
      action: 'Optimize existing quizzes for better difficulty distribution',
      impact: 'Fine-tunes learning progression',
      estimatedTime: '2-4 hours'
    });
  }

  return actions;
};

/**
 * Enhance course type breakdown with status indicators
 */
const enhanceCourseTypeBreakdown = (breakdown: any) => {
  const enhanced: any = {};

  Object.entries(breakdown).forEach(([type, data]: any) => {
    let status: 'excellent' | 'good' | 'needs-improvement' | 'critical';

    if (data.quizCoverage >= 90 && data.averageQuality >= 80) {
      status = 'excellent';
    } else if (data.quizCoverage >= 70 && data.averageQuality >= 70) {
      status = 'good';
    } else if (data.quizCoverage >= 50 || data.averageQuality >= 60) {
      status = 'needs-improvement';
    } else {
      status = 'critical';
    }

    enhanced[type] = {
      ...data,
      totalModules: 0, // Will be calculated from course results
      status
    };
  });

  return enhanced;
};

/**
 * Print audit summary to console
 */
const printAuditSummary = (report: FullAuditReport) => {
  console.log('\n' + '='.repeat(70));
  console.log('📊 FULL QUIZ AUDIT REPORT - EXECUTIVE SUMMARY');
  console.log('='.repeat(70));
  console.log(`\n⏱️  Execution Time: ${report.executionTime}ms`);
  console.log(`📅 Generated: ${new Date(report.timestamp).toLocaleString()}\n`);

  console.log('📈 OVERALL STATISTICS:');
  console.log(`   Total Courses: ${report.summary.totalCourses}`);
  console.log(`   Total Modules: ${report.summary.totalModules}`);
  console.log(`   Modules with Quizzes: ${report.summary.modulesWithQuizzes}`);
  console.log(`   Modules without Quizzes: ${report.summary.modulesWithoutQuizzes}`);
  console.log(`   Coverage: ${report.summary.coveragePercentage}%`);
  console.log(`   Quality Grade: ${report.summary.qualityGrade} (${report.summary.overallQualityScore}%)\n`);

  console.log('🎯 MISSING QUIZZES BY PRIORITY:');
  console.log(`   🔴 CRITICAL: ${report.missingQuizzesByPriority.critical.length} modules`);
  console.log(`   🟠 HIGH: ${report.missingQuizzesByPriority.high.length} modules`);
  console.log(`   🟡 MEDIUM: ${report.missingQuizzesByPriority.medium.length} modules`);
  console.log(`   🟢 LOW: ${report.missingQuizzesByPriority.low.length} modules\n`);

  console.log('⚡ TOP PRIORITY ACTIONS:');
  report.priorityActions.slice(0, 3).forEach((action, i) => {
    const emoji = action.priority === 'CRITICAL' ? '🔴' : action.priority === 'HIGH' ? '🟠' : '🟡';
    console.log(`   ${emoji} ${i + 1}. [${action.priority}] ${action.action}`);
    console.log(`      Impact: ${action.impact}`);
    console.log(`      Est. Time: ${action.estimatedTime}\n`);
  });

  console.log('📊 COURSE TYPE STATUS:');
  const sortedTypes = Object.entries(report.courseTypeBreakdown)
    .sort(([, a]: any, [, b]: any) => a.quizCoverage - b.quizCoverage)
    .slice(0, 10);

  sortedTypes.forEach(([type, data]: any) => {
    const statusEmoji = 
      data.status === 'excellent' ? '✅' :
      data.status === 'good' ? '👍' :
      data.status === 'needs-improvement' ? '⚠️' : '🔴';
    
    console.log(`   ${statusEmoji} ${type.toUpperCase()}: ${data.quizCoverage.toFixed(1)}% coverage, ${data.averageQuality.toFixed(1)}% quality`);
  });

  console.log('\n' + '='.repeat(70));
  console.log('💡 Next Steps: Review priorityActions array for detailed action plan');
  console.log('📄 Full report available in returned object');
  console.log('='.repeat(70) + '\n');
};

/**
 * Export audit data to JSON file (for analysis or archival)
 */
export const exportAuditToJSON = (report: FullAuditReport): string => {
  return JSON.stringify(report, null, 2);
};

/**
 * Get quick stats (for dashboard widgets)
 */
export const getQuickAuditStats = () => {
  const audit = comprehensiveQuizAuditor.auditAllQuizzes();
  const coverage = (audit.modulesWithQuizzes / audit.totalModules) * 100;

  return {
    coverage: parseFloat(coverage.toFixed(1)),
    quality: parseFloat(audit.overallQualityScore.toFixed(1)),
    missingCount: audit.modulesWithoutQuizzes,
    totalModules: audit.totalModules,
    status: coverage >= 90 ? 'excellent' : coverage >= 70 ? 'good' : coverage >= 50 ? 'needs-work' : 'critical'
  };
};

// Auto-run in development mode (optional)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  // Expose to window for easy console access
  (window as any).runQuizAudit = runFullQuizAudit;
  (window as any).getQuickAuditStats = getQuickAuditStats;
  
  console.log('💡 Quiz Audit Tools Available:');
  console.log('   - window.runQuizAudit() - Run full audit');
  console.log('   - window.getQuickAuditStats() - Get quick stats');
}
